import { supabase } from '@/lib/supabase'
import type { OpportunityItem } from '@/modules/oportunidades/types'
import { listConfluences, confluencePercent } from '@/modules/oportunidades/utils/confluence'
import { fetchAssetsMeta } from '@/modules/oportunidades/utils/assets'

export type AuditReport = {
  id: string
  opportunity_id: number
  report_text: string
  reported_by: string
  reported_email?: string | null
  reported_role?: string | null
  reported_name?: string | null
  status?: string | null
  created_at: string
}

export async function reportBug(opportunityId: string | number, text: string): Promise<void> {
  const { data: userData, error: authErr } = await supabase.auth.getUser()
  if (authErr) throw authErr
  const uid = userData?.user?.id
  if (!uid) throw new Error('Usuário não autenticado')
  const email = userData?.user?.email || null
  const role = (userData?.user as any)?.user_metadata?.role || null
  const payload = {
    opportunity_id: Number(opportunityId),
    report_text: String(text || '').trim(),
    reported_by: uid,
    reported_email: email,
    reported_role: role,
  }
  if (!payload.report_text) throw new Error('Descreva o problema antes de enviar')
  const { error } = await supabase.from('opportunity_audits').insert(payload as any)
  if (error) throw error
}

export async function markOpportunityAuditResolved(opportunityId: string | number): Promise<void> {
  // Master-only by RLS: update all reports of this opportunity to reviewed
  const { error } = await supabase
    .from('opportunity_audits')
    .update({ status: 'reviewed' })
    .eq('opportunity_id', Number(opportunityId))
  if (error) throw error
}

export async function getMyLatestReport(opportunityId: string | number): Promise<AuditReport | null> {
  const { data: userData, error: authErr } = await supabase.auth.getUser()
  if (authErr) throw authErr
  const uid = userData?.user?.id
  if (!uid) throw new Error('Usuário não autenticado')
  const { data, error } = await supabase
    .from('opportunity_audits')
    .select('id, opportunity_id, report_text, reported_by, reported_email, reported_role, status, created_at')
    .eq('opportunity_id', Number(opportunityId))
    .eq('reported_by', uid)
    .order('created_at', { ascending: false })
    .limit(1)
  if (error) throw error
  const row = (Array.isArray(data) ? data[0] : null) as any
  if (!row) return null
  return {
    id: String(row.id),
    opportunity_id: Number(row.opportunity_id),
    report_text: String(row.report_text || ''),
    reported_by: String(row.reported_by || ''),
    reported_email: row.reported_email || null,
    reported_role: row.reported_role || null,
    reported_name: null,
    status: row.status || null,
    created_at: row.created_at,
  }
}

export async function updateReport(reportId: string | number, text: string): Promise<void> {
  const { error } = await supabase
    .from('opportunity_audits')
    .update({ report_text: String(text || '').trim() })
    .eq('id', Number(reportId))
  if (error) throw error
}

// Lista oportunidades com auditoria (abertas ou fechadas)
export async function listAuditedOpportunities(page = 1, pageSize = 25): Promise<{ items: OpportunityItem[]; total: number; totalPages: number; }> {
  const from = Math.max(0, (page - 1) * pageSize)
  const to = from + pageSize - 1
  try {
    const { data: rows, error, count } = await supabase
      .from('v_opportunities_audited')
      .select('*', { count: 'exact' })
      .order('last_report_at', { ascending: false })
      .range(from, to)
    if (error) throw error
    const symbols = Array.from(new Set((rows || []).map((r:any)=> String(r.symbol||'').toUpperCase()).filter(Boolean)))
    const assets = await fetchAssetsMeta(symbols)
    const bySymbol = new Map(assets.map((a) => [String(a.symbol).toUpperCase(), a]))
    const items: OpportunityItem[] = (rows || []).map((r: any) => mapRowToItem(r, bySymbol))
    const total = count || 0
    const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1
    return { items, total, totalPages }
  } catch (e: any) {
    // Fallback quando a view ainda não existe: agrega via opportunity_audits
    return await listAuditedOpportunitiesFallback(page, pageSize)
  }
}

async function listAuditedOpportunitiesFallback(page = 1, pageSize = 25): Promise<{ items: OpportunityItem[]; total: number; totalPages: number; }> {
  // Busca os últimos relatos e agrega por oportunidade (ordem pelo último relato)
  const MAX_SCAN = 2000
  const { data: audits, error: e1 } = await supabase
    .from('opportunity_audits')
    .select('opportunity_id, created_at')
    .order('created_at', { ascending: false })
    .limit(MAX_SCAN)
  if (e1) throw e1
  const metaById = new Map<number, { last: string; count: number }>()
  for (const r of audits || []) {
    const id = Number((r as any).opportunity_id)
    const ts = String((r as any).created_at)
    if (!metaById.has(id)) metaById.set(id, { last: ts, count: 1 })
    else metaById.get(id)!.count += 1
  }
  const orderedIds = Array.from(metaById.entries()).sort((a,b)=> new Date(b[1].last).getTime() - new Date(a[1].last).getTime()).map(([id])=> id)
  const total = orderedIds.length
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1
  const pageIds = orderedIds.slice((page-1)*pageSize, (page-1)*pageSize + pageSize)
  if (pageIds.length === 0) return { items: [], total, totalPages }
  const { data: rows, error: e2 } = await supabase
    .from('trading_opportunities')
    .select('id,symbol,timeframe,target_timeframe,opportunity_type,signal_type,target_trend,confluence_strength,confluence_factors,created_at,open_time,status,closed_at,closed_reason')
    .in('id', pageIds as any)
  if (e2) throw e2
  const symbols = Array.from(new Set((rows || []).map((r:any)=> String(r.symbol||'').toUpperCase()).filter(Boolean)))
  const assets = await fetchAssetsMeta(symbols)
  const bySymbol = new Map(assets.map((a) => [String(a.symbol).toUpperCase(), a]))
  // Manter ordenação por last_report_at
  const byIdRow = new Map((rows||[]).map((r:any)=> [Number(r.id), r]))
  const orderedRows = pageIds.map((id)=> byIdRow.get(id)).filter(Boolean)
  const items: OpportunityItem[] = orderedRows.map((r: any) => mapRowToItem(r, bySymbol))
  return { items, total, totalPages }
}

export async function listAuditedOpportunitiesByStatus(status: 'pending'|'resolved', page = 1, pageSize = 25): Promise<{ items: OpportunityItem[]; total: number; totalPages: number; }> {
  // Build aggregation of audits
  const MAX_SCAN = 5000
  const { data: audits, error: e1 } = await supabase
    .from('opportunity_audits')
    .select('opportunity_id, status, created_at')
    .order('created_at', { ascending: false })
    .limit(MAX_SCAN)
  if (e1) throw e1
  const stateById = new Map<number, { last: string; hasPending: boolean }>()
  const countById = new Map<number, number>()
  for (const r of audits || []) {
    const id = Number((r as any).opportunity_id)
    const ts = String((r as any).created_at)
    const st = String((r as any).status || 'pending')
    const cur = stateById.get(id)
    const hasPending = st !== 'reviewed'
    if (!cur) stateById.set(id, { last: ts, hasPending })
    else stateById.set(id, { last: cur.last > ts ? cur.last : ts, hasPending: cur.hasPending || hasPending })
    countById.set(id, (countById.get(id) || 0) + 1)
  }
  // Filter by tab status
  const filteredIds = Array.from(stateById.entries())
    .filter(([_, v]) => status === 'pending' ? v.hasPending : !v.hasPending)
    .sort((a,b)=> new Date(b[1].last).getTime() - new Date(a[1].last).getTime())
    .map(([id])=> id)
  const total = filteredIds.length
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1
  const pageIds = filteredIds.slice((page-1)*pageSize, (page-1)*pageSize + pageSize)
  if (pageIds.length === 0) return { items: [], total, totalPages }
  const { data: rows, error: e2 } = await supabase
    .from('trading_opportunities')
    .select('id,symbol,timeframe,target_timeframe,opportunity_type,signal_type,target_trend,confluence_strength,confluence_factors,created_at,open_time,status,closed_at,closed_reason')
    .in('id', pageIds as any)
  if (e2) throw e2
  const symbols = Array.from(new Set((rows || []).map((r:any)=> String(r.symbol||'').toUpperCase()).filter(Boolean)))
  const assets = await fetchAssetsMeta(symbols)
  const bySymbol = new Map(assets.map((a) => [String(a.symbol).toUpperCase(), a]))
  const byIdRow = new Map((rows||[]).map((r:any)=> [Number(r.id), r]))
  const orderedRows = pageIds.map((id)=> byIdRow.get(id)).filter(Boolean)
  const items: OpportunityItem[] = orderedRows.map((r: any) => ({ ...mapRowToItem(r, bySymbol), auditCount: countById.get(Number(r.id)) || 0 } as any))
  return { items, total, totalPages }
}

export async function listReportsByOpportunity(opportunityId: string | number): Promise<AuditReport[]> {
  const { data, error } = await supabase
    .from('opportunity_audits')
    .select('id, opportunity_id, report_text, reported_by, reported_email, reported_role, status, created_at')
    .eq('opportunity_id', Number(opportunityId))
    .order('created_at', { ascending: false })
  if (error) throw error
  const rows = (data || []) as any[]
  // Enriquecer com nome a partir de profiles (apenas se disponível)
  let namesById = new Map<string, string | null>()
  try {
    const ids = Array.from(new Set(rows.map(r => String(r.reported_by || '').trim()).filter(Boolean)))
    if (ids.length) {
      const { data: profs } = await supabase.from('profiles').select('id, full_name').in('id', ids)
      namesById = new Map((profs || []).map((p: any) => [String(p.id), (p.full_name || null) as string | null]))
    }
  } catch (e) {
    // ignorar falhas de RLS para não master
  }
  return rows.map((r: any) => ({
    id: String(r.id),
    opportunity_id: Number(r.opportunity_id),
    report_text: String(r.report_text || ''),
    reported_by: String(r.reported_by || ''),
    reported_email: r.reported_email || null,
    reported_role: r.reported_role || null,
    reported_name: namesById.get(String(r.reported_by || '')) || null,
    status: r.status || null,
    created_at: r.created_at,
  }))
}

// Helpers locais (reduzidos) para mapear a mesma UI de Oportunidades
function tfShort(tf?: string | null): string | null {
  const t = String(tf || '').toUpperCase()
  if (!t) return null
  if (t === '5') return '5min'
  if (t === '15') return '15min'
  if (t === '30') return '30min'
  if (t === '60') return '1h'
  if (t === '240') return '4h'
  if (t === '720') return '12h'
  if (t === 'D') return '1D'
  if (t === 'W') return '1W'
  if (t === 'M') return '1M'
  return t
}

function toObjective(trend?: string | null): 'Long' | 'Short' | '' {
  const t = String(trend || '').toLowerCase()
  if (t === 'bullish' || t === 'alta' || t === 'long') return 'Long'
  if (t === 'bearish' || t === 'baixa' || t === 'short') return 'Short'
  return ''
}

function mapRowToItem(r: any, bySymbol?: Map<string, any>): OpportunityItem {
  const strength = Number(r?.confluence_strength || 0)
  const pct = confluencePercent(strength, r?.confluence_factors)
  const sym = String(r.symbol || '').toUpperCase()
  const meta = bySymbol?.get(sym)
  return {
    id: String(r.id),
    symbol: sym,
    name: meta?.name || meta?.base_asset || null,
    logo_url: meta?.logo_url || null,
    recommendation: null,
    objective: toObjective(String(r?.target_trend || (r?.confluence_factors?.target_trend ?? ''))),
    confluencePercent: pct,
    confluenceStrength: strength,
    status: (r as any)?.status ? String((r as any).status) : (r?.closed_at ? 'closed' : 'active'),
    fractalLower: tfShort(String((r as any)?.timeframe || '')) || undefined,
    fractalUpper: tfShort(String((r as any)?.target_timeframe || '')) || undefined,
    nearBreak: false,
    confluences: listConfluences(r?.confluence_factors),
    uiWarning: false,
  }
}
